/** ViewBox pan/zoom helpers for the floorplan SVG viewport. */

export interface ViewBox {
    vx: number;
    vy: number;
    vw: number;
    vh: number;
}

/** Zoom relative to fit-to-screen: 0.3× … 4× per product requirements. */
export const MIN_ZOOM = 0.3;
export const MAX_ZOOM = 4;

export function cloneView(v: ViewBox): ViewBox {
    return { vx: v.vx, vy: v.vy, vw: v.vw, vh: v.vh };
}

export function ensureContentBox(svg: SVGSVGElement): ViewBox {
    const wAttr = parseFloat(svg.getAttribute("width") ?? "");
    const hAttr = parseFloat(svg.getAttribute("height") ?? "");
    if (!svg.getAttribute("viewBox") && wAttr > 0 && hAttr > 0) {
        svg.setAttribute("viewBox", `0 0 ${wAttr} ${hAttr}`);
    }
    const b = svg.viewBox.baseVal;
    if (b && b.width > 0 && b.height > 0) {
        return { vx: b.x, vy: b.y, vw: b.width, vh: b.height };
    }
    try {
        const bb = svg.getBBox();
        const box = { vx: bb.x, vy: bb.y, vw: bb.width, vh: bb.height };
        svg.setAttribute("viewBox", `${box.vx} ${box.vy} ${box.vw} ${box.vh}`);
        return box;
    } catch {
        return { vx: 0, vy: 0, vw: 1, vh: 1 };
    }
}

export function applyViewBox(svg: SVGSVGElement, view: ViewBox): void {
    svg.setAttribute("viewBox", `${view.vx} ${view.vy} ${view.vw} ${view.vh}`);
}

export function fitView(content: ViewBox): ViewBox {
    return cloneView(content);
}

export function currentZoom(content: ViewBox, view: ViewBox): number {
    if (view.vw <= 0) return 1;
    return content.vw / view.vw;
}

export function clampViewDims(content: ViewBox, width: number): { vw: number; vh: number } {
    const maxW = content.vw / MIN_ZOOM;
    const minW = content.vw / MAX_ZOOM;
    const vw = Math.min(maxW, Math.max(minW, width));
    const vh = vw * (content.vh / content.vw);
    return { vw, vh };
}

export function svgPointFromClient(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
): { x: number; y: number } | null {
    if (!svg.createSVGPoint) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    try {
        const mapped = pt.matrixTransform(ctm.inverse());
        return { x: mapped.x, y: mapped.y };
    } catch {
        return null;
    }
}

export function zoomAtPoint(
    content: ViewBox,
    view: ViewBox,
    svg: SVGSVGElement,
    clientX: number,
    clientY: number,
    factor: number,
): ViewBox {
    const loc = svgPointFromClient(svg, clientX, clientY);
    if (!loc) return view;
    const next = clampViewDims(content, view.vw / factor);
    const r = next.vw / view.vw;
    return {
        vx: loc.x - (loc.x - view.vx) * r,
        vy: loc.y - (loc.y - view.vy) * r,
        vw: next.vw,
        vh: next.vh,
    };
}

export function elementBBoxInSvgSpace(
    el: SVGGraphicsElement,
    svg: SVGSVGElement,
): { x: number; y: number; width: number; height: number } | null {
    let bb: DOMRect;
    try {
        bb = el.getBBox();
    } catch {
        return null;
    }
    if (!bb.width && !bb.height) return null;

    const elCtm = el.getScreenCTM?.();
    const rootCtm = svg.getScreenCTM?.();
    if (!elCtm || !rootCtm || !svg.createSVGPoint) {
        return { x: bb.x, y: bb.y, width: bb.width, height: bb.height };
    }
    let toRoot: DOMMatrix;
    try {
        toRoot = rootCtm.inverse().multiply(elCtm);
    } catch {
        return { x: bb.x, y: bb.y, width: bb.width, height: bb.height };
    }

    const corners: [number, number][] = [
        [bb.x, bb.y],
        [bb.x + bb.width, bb.y],
        [bb.x, bb.y + bb.height],
        [bb.x + bb.width, bb.y + bb.height],
    ];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of corners) {
        const pt = svg.createSVGPoint();
        pt.x = x;
        pt.y = y;
        const t = pt.matrixTransform(toRoot);
        minX = Math.min(minX, t.x);
        minY = Math.min(minY, t.y);
        maxX = Math.max(maxX, t.x);
        maxY = Math.max(maxY, t.y);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function centerOnElement(
    content: ViewBox,
    svg: SVGSVGElement,
    el: SVGGraphicsElement,
    stageWidth: number,
    stageHeight: number,
): ViewBox {
    const bb = elementBBoxInSvgSpace(el, svg);
    if (!bb || !bb.width || !bb.height) return fitView(content);
    const targetCx = bb.x + bb.width / 2;
    const targetCy = bb.y + bb.height / 2;
    const minDim = Math.min(stageWidth, stageHeight);
    const zoomFactor = Math.min(MAX_ZOOM, minDim / Math.max(bb.width, bb.height) / 4);
    const dims = clampViewDims(content, content.vw / Math.max(zoomFactor, MIN_ZOOM));
    return {
        vw: dims.vw,
        vh: dims.vh,
        vx: targetCx - dims.vw / 2,
        vy: targetCy - dims.vh / 2,
    };
}
